var CHUNKED_THRESHOLD = 20 * 1024 * 1024; // files > 20 MB use chunked upload
var MAX_RETRIES = 3;
var RETRY_DELAYS = [0, 1000, 3000]; // ms before each retry attempt
var CONCURRENCY = 3;

function getChunkSize(fileSize) {
    if (fileSize > 2 * 1024 * 1024 * 1024) return 100 * 1024 * 1024;
    if (fileSize > 500 * 1024 * 1024) return 50 * 1024 * 1024;
    return 20 * 1024 * 1024;
}

function getFileFingerprint(file) {
    return 'upload_' + file.name + '_' + file.size + '_' + file.lastModified;
}

function findPendingUpload(fingerprint) {
    try {
        return localStorage.getItem(fingerprint) || null;
    } catch (e) { return null; }
}

function savePendingUpload(fingerprint, uploadId) {
    try {
        localStorage.setItem(fingerprint, uploadId);
    } catch (e) { /* ignore */ }
}

function removePendingUpload(fingerprint) {
    try {
        localStorage.removeItem(fingerprint);
    } catch (e) { /* ignore */ }
}

function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
}

function throwIfCancelled(cancelToken) {
    if (cancelToken && cancelToken.reason) {
        throw cancelToken.reason;
    }
}

function makeProgressReport(uploadedBytes, totalBytes, startTime, processing) {
    var percent = totalBytes > 0 ? Math.min(99, Math.round((uploadedBytes / totalBytes) * 100)) : 0;
    var elapsed = (Date.now() - startTime) / 1000;
    var speed = elapsed > 0.5 ? uploadedBytes / elapsed / (1024 * 1024) : 0;
    var remaining = speed > 0 ? Math.round((totalBytes - uploadedBytes) / (speed * 1024 * 1024)) : 0;
    return {
        percent: processing ? 100 : percent,
        speed: Math.round(speed * 10) / 10,
        remaining: remaining,
        processing: !!processing,
        active: true,
        uploadedBytes: uploadedBytes,
        totalBytes: totalBytes
    };
}

function uploadChunkWithRetry(url, formData, cancelToken, attempt, onChunkProgress) {
    attempt = attempt || 0;
    throwIfCancelled(cancelToken);
    var doAttempt = function () {
        throwIfCancelled(cancelToken);
        return axios.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            cancelToken: cancelToken,
            onUploadProgress: function (e) { if (onChunkProgress) onChunkProgress(e.loaded); }
        });
    };

    var run = (RETRY_DELAYS[attempt] > 0)
        ? delay(RETRY_DELAYS[attempt]).then(doAttempt)
        : doAttempt();

    return run.catch(function (err) {
        if (axios.isCancel(err)) throw err;
        if (attempt < MAX_RETRIES - 1) {
            return uploadChunkWithRetry(url, formData, cancelToken, attempt + 1, onChunkProgress);
        }
        throw err;
    });
}

function chunkedUpload(file, onProgress, baseUrl, cancelToken) {
    baseUrl = baseUrl || 'api/tickets';
    onProgress({ percent: 0, speed: 0, remaining: 0, processing: false, active: true, uploadedBytes: 0, totalBytes: file.size });

    if (file.size <= CHUNKED_THRESHOLD) {
        return simpleUpload(file, onProgress, baseUrl, cancelToken);
    }

    var chunkSize = getChunkSize(file.size);
    var totalChunks = Math.ceil(file.size / chunkSize);
    var fingerprint = getFileFingerprint(file);
    var pendingUploadId = findPendingUpload(fingerprint);

    var getUploadId;
    if (pendingUploadId) {
        getUploadId = axios.get(baseUrl + '/upload/status/' + pendingUploadId)
            .then(function (statusResponse) {
                if (statusResponse.data.total_chunks === totalChunks) {
                    return {
                        uploadId: pendingUploadId,
                        alreadyUploaded: statusResponse.data.uploaded_chunks || []
                    };
                }
                removePendingUpload(fingerprint);
                return null;
            })
            .catch(function (err) {
                if (axios.isCancel(err)) throw err;
                removePendingUpload(fingerprint);
                return null;
            });
    } else {
        getUploadId = Promise.resolve(null);
    }

    return getUploadId.then(function (resumeData) {
        throwIfCancelled(cancelToken);
        if (resumeData) {
            return resumeData;
        }
        return axios.post(baseUrl + '/upload/init', {
            file_name: file.name,
            file_size: file.size,
            total_chunks: totalChunks
        }).then(function (initResponse) {
            var uploadId = initResponse.data.upload_id;
            savePendingUpload(fingerprint, uploadId);
            return { uploadId: uploadId, alreadyUploaded: [] };
        });
    }).then(function (uploadInfo) {
        var uploadId = uploadInfo.uploadId;
        var alreadyUploadedCount = 0;
        var uploadedChunks = {};
        (uploadInfo.alreadyUploaded).forEach(function (idx) {
            uploadedChunks[idx] = true;
            alreadyUploadedCount++;
        });

        var startTime = Date.now();
        var fileSize = file.size;
        var completedBytes = alreadyUploadedCount * chunkSize;
        var workerBytes = [];
        for (var wi = 0; wi < CONCURRENCY; wi++) workerBytes.push(0);
        var queue = [];
        for (var i = 0; i < totalChunks; i++) {
            if (!uploadedChunks[i]) {
                queue.push(i);
            }
        }

        function reportProgress() {
            var totalLoaded = completedBytes;
            for (var j = 0; j < workerBytes.length; j++) totalLoaded += workerBytes[j];
            onProgress(makeProgressReport(Math.min(totalLoaded, fileSize), fileSize, startTime));
        }

        if (completedBytes > 0) {
            reportProgress();
        }

        function worker(workerIdx) {
            if (queue.length === 0) return Promise.resolve();
            throwIfCancelled(cancelToken);
            var idx = queue.shift();
            var start = idx * chunkSize;
            var actualSize = Math.min(chunkSize, fileSize - start);
            var chunk = file.slice(start, start + actualSize);
            var formData = new FormData();
            formData.append('upload_id', uploadId);
            formData.append('chunk_index', idx);
            formData.append('file', chunk);
            workerBytes[workerIdx] = 0;
            return uploadChunkWithRetry(baseUrl + '/upload/chunk', formData, cancelToken, 0, function (loaded) {
                workerBytes[workerIdx] = Math.min(loaded, actualSize);
                reportProgress();
            }).then(function () {
                    workerBytes[workerIdx] = 0;
                    completedBytes += actualSize;
                    reportProgress();
                    return worker(workerIdx);
                });
        }

        var workers = [];
        for (var w = 0; w < CONCURRENCY; w++) {
            (function(idx) {
                workers.push(
                    (idx > 0 ? delay(idx * 300) : Promise.resolve()).then(function() { return worker(idx); })
                );
            })(w);
        }

        return Promise.all(workers).then(function () {
            throwIfCancelled(cancelToken);
            onProgress(makeProgressReport(fileSize, fileSize, startTime, true));
            return axios.post(baseUrl + '/upload/complete', {
                upload_id: uploadId
            }, { timeout: 300000 });
        }).then(function (completeResponse) {
            removePendingUpload(fingerprint);
            return completeResponse.data;
        }).catch(function (err) {
            if (axios.isCancel(err) && uploadId) {
                removePendingUpload(fingerprint);
                axios.post(baseUrl + '/upload/cancel', { upload_id: uploadId }).catch(function () {});
            }
            throw err;
        });
    });
}

function simpleUpload(file, onProgress, baseUrl, cancelToken) {
    var startTime = Date.now();
    var fileSize = file.size;
    var formData = new FormData();
    formData.append('file', file);
    return axios.post(baseUrl + '/attachments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        cancelToken: cancelToken,
        onUploadProgress: function (progressEvent) {
            onProgress(makeProgressReport(progressEvent.loaded, fileSize, startTime));
        }
    }).then(function (response) {
        return response.data;
    });
}

window.chunkedUpload = chunkedUpload;

export default chunkedUpload;
